/**
 * WordExpress Landing Page - WITH LOGO!
 * 
 * Complete landing page with hero, features, pricing, and CTA
 * Brand color: #6B46C1 (purple)
 * 
 * UPDATED: Added WordExpress logo to hero section!
 * 
 * PRICES:
 * - Quick Themes: $49
 * - AI Custom: $149
 * - Studio Pack: $299 (3 themes)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import PaletteIcon from '@mui/icons-material/Palette';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(12, 0, 8, 0),
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0, 6, 0),
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(3),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
  },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
}));

const PricingCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
  border: '2px solid transparent',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'scale(1.02)',
  },
}));

const CTASection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(10, 0),
  textAlign: 'center',
}));

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [logoError, setLogoError] = useState(false);

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Lightning Fast',
      description: 'Your theme is ready in 5–15 minutes. Quick Themes load even faster with instant caching after the first run.'
    },
    {
      icon: <PaletteIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Fully Customizable',
      description: 'Choose your industry, pages, colors, layouts, and typography. Built to fit what you do, not just any website.'
    },
    {
      icon: <CodeOffIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'No Coding Required',
      description: 'A complete, installable WordPress theme without writing a single line of code. Perfect for businesses, agencies, and entrepreneurs.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Pay Only When It\'s Ready',
      description: 'You\'re only charged when your theme is complete and ready to download. If generation fails, you pay nothing and get free retries.'
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Built for your industry, not just any website',
      description: 'Every theme is shaped around your specific industry — the pages you need, the features that make sense, the content that fits.'
    },
    {
      icon: <RocketLaunchIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Industry-Specific',
      description: '10 industries, 45+ specializations. Healthcare, Legal, Construction, Restaurants, and more. Built for your niche.'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Choose Your Path',
      description: 'Quick Themes for instant layouts, or a Custom Theme built specifically for your industry.'
    },
    {
      step: 2,
      title: 'Select & Customize',
      description: 'Pick your industry, specialization, pages, colors, and design preferences.'
    },
    {
      step: 3,
      title: 'Your Theme Comes Together',
      description: 'Watch real-time progress as your theme is built — pages, content, styles, and all.'
    },
    {
      step: 4,
      title: 'Download & Install',
      description: 'Get your custom WordPress theme as a ZIP file. Install instantly on your site.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          {/* HERO LOGO - glass-card wrapper for readability on purple background */}
          {!logoError && (
            <Box
              sx={{
                mb: 4,
                display: 'flex',
                justifyContent: 'center',
                animation: 'fadeIn 0.8s ease-in',
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: isMobile ? '10px 12px' : '14px 18px',
                  borderRadius: isMobile ? '14px' : '16px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  boxShadow: '0 18px 45px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                  },
                }}
              >
                <img
                  src="/assets/images/WordExPress_logo.png"
                  alt="WordExPress - Build Your Own Custom WordPress Theme"
                  style={{
                    display: 'block',
                    maxWidth: isMobile ? 'min(260px, 78vw)' : 'min(480px, 85vw)',
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.35))',
                  }}
                  onError={() => setLogoError(true)}
                />
              </Box>
            </Box>
          )}
          
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            A WordPress Theme Built for Your Business, Ready Today
          </Typography>

          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{ mb: 4, opacity: 0.95 }}
          >
            You only pay when your theme is ready. No results, no charge.
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            sx={{ mb: 6 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              Build Your Theme
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              View Pricing
            </Button>
          </Stack>

          {/* Value proposition line */}
          <Typography
            variant="body1"
            sx={{ mb: 2, opacity: 0.9, fontStyle: 'italic' }}
          >
            Built around your industry. Installed in under 15 minutes. Yours to keep.
          </Typography>
          
          {/* Trust Indicators */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} sm={4}>
              <StatsBox>
                <Typography variant="h3" fontWeight="bold">
                  ~10 min
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Average Generation Time
                </Typography>
              </StatsBox>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <StatsBox>
                <Typography variant="h3" fontWeight="bold">
                  45+
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Industries Covered
                </Typography>
              </StatsBox>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <StatsBox>
                <Typography variant="h3" fontWeight="bold">
                  100%
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Pay Only When It's Ready
                </Typography>
              </StatsBox>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          textAlign="center" 
          fontWeight="bold" 
          gutterBottom
        >
          Why WordExpress?
        </Typography>
        
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="textSecondary" 
          sx={{ mb: 6 }}
        >
          A custom theme built for your business, without the developer price tag
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard elevation={2}>
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {feature.description}
                </Typography>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            textAlign="center" 
            fontWeight="bold" 
            gutterBottom
          >
            How It Works
          </Typography>
          
          <Typography 
            variant="h6" 
            textAlign="center" 
            color="textSecondary" 
            sx={{ mb: 6 }}
          >
            From idea to your own WordPress theme in 4 simple steps
          </Typography>
          
          <Grid container spacing={4}>
            {howItWorks.map((step) => (
              <Grid item xs={12} sm={6} md={3} key={step.step}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {step.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Preview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          textAlign="center" 
          fontWeight="bold" 
          gutterBottom
        >
          Simple, Transparent Pricing
        </Typography>
        
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="textSecondary" 
          sx={{ mb: 6 }}
        >
          Choose the plan that fits your needs
        </Typography>
        
        <Grid container spacing={4}>
          {/* Quick Theme */}
          <Grid item xs={12} md={4}>
            <PricingCard elevation={2}>
              <Chip label="Fast Start" color="success" sx={{ mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Quick Theme
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                $49
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                one-time
              </Typography>

              <Stack spacing={2} sx={{ mb: 3, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Polished, ready-to-install theme</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">7 pages with your branding applied</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">6 visual styles, 12 color schemes each</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Responsive + SEO optimized</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Pay only when it's ready</Typography>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => navigate('/quick-themes')}
              >
                Start Quick Theme
              </Button>
            </PricingCard>
          </Grid>

          {/* Custom Theme */}
          <Grid item xs={12} md={4}>
            <PricingCard elevation={2}>
              <Chip label="Most Popular" color="primary" sx={{ mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Custom Theme
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                $149
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                one-time
              </Typography>

              <Stack spacing={2} sx={{ mb: 3, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Theme shaped around your industry</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Up to 70 industry-specific pages</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Content written for your business</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Industry-appropriate features & layouts</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Responsive + SEO optimized</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Pay only when it's ready</Typography>
                </Box>
              </Stack>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => navigate('/ai-custom')}
              >
                Start Custom Theme
              </Button>
            </PricingCard>
          </Grid>

          {/* Studio Pack */}
          <Grid item xs={12} md={4}>
            <PricingCard elevation={2}>
              <Chip label="Best Value" color="secondary" sx={{ mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Studio Pack
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                $299
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                one-time · 3 themes
              </Typography>

              <Stack spacing={2} sx={{ mb: 3, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">3 Custom Themes — any mix of industries</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Same as Custom Theme, ×3</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Great for client work</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">Credits never expire</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="body2">No subscriptions, no recurring fees</Typography>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => navigate('/pricing')}
              >
                Get Studio Pack
              </Button>
            </PricingCard>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
            Ready to get a theme that actually fits your business?
          </Typography>

          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            45+ industries covered. Ready to install in under 15 minutes. No coding, no risk.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              backgroundColor: 'white',
              color: 'primary.main',
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            Build Your Theme
          </Button>
        </Container>
      </CTASection>
    </Box>
  );
};

export default LandingPage;